import { useEffect } from "react";
import { styled } from "styled-components";

import { ErrorMessage, Image, Link, StyledModalText, UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import { openMapInvitation } from "domains/createShareSettings/actions";
import { getIsOpenLinkPending, getOpenLinkErrorStatus, getOpenLinkInvite } from "domains/createShareSettings/selectors";
import { interpolate } from "domains/language/interpolate";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";

const StyledImage = styled(Image)`
  width: 100%;
  height: 150px;
  border-radius: 5px;
  margin-bottom: 1rem;
`;

export const OpenLinkInvitationModal = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const translate = useTranslate();
  const { token } = useSelector(getModalParams) as ModalParams<"OpenLinkInvitation">;
  const invite = useSelector(getOpenLinkInvite);
  const isPending = useSelector(getIsOpenLinkPending);
  const errorStatus = useSelector(getOpenLinkErrorStatus);

  useEffect(() => {
    if (token) {
      dispatch(openMapInvitation(token));
    }
  }, [dispatch, token]);

  const nameLabel = () => <strong>{invite?.topic_name}</strong>;
  const ownerLabel = () => <strong>{invite?.topic_creator_name}</strong>;

  const inviteText = interpolate(translate("app.map_share.invite_link.message"), {
    name: nameLabel,
    owner: ownerLabel,
  });

  const onClick = (topic_id: string) => () => {
    go(`/topics/${topic_id}`);
    dispatch(deactivateModal());
  };

  const getErrorText = () => {
    switch (errorStatus) {
      case 404:
        return translate("app.notifications.map_invite.topic_deleted");
      case 409:
        return translate("app.topic.invite_link.conflict_error");
      default:
        return interpolate(translate("app.topic.follow.error_message"), { supportEmail });
    }
  };

  return (
    <>
      <ModalHeader title={translate("app.notification.map_share.title")} />
      {isPending && <StyledModalText>{translate("app.loading")}</StyledModalText>}
      {invite !== undefined && (
        <>
          <StyledImage src={invite.topic_images?.thumbnail} alt={invite.topic_name} />
          <StyledModalText>{inviteText}</StyledModalText>
          <UIButton onClick={onClick(invite.topic_id)} variant="filled" fullwidth>
            {translate("app.topic.view_transformation_map")}
          </UIButton>
        </>
      )}
      <ErrorMessage isActive={errorStatus !== undefined}>{errorStatus !== undefined && getErrorText()}</ErrorMessage>
    </>
  );
};

const supportEmail = (supportEmail: string) => <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>;
